// Load/save with validation, versioning and migration from the v1 MVP format.

import { resolveIngredient, type IngredientId } from '../domain/taxonomy';
import type { DietRuleId } from '../domain/diet';
import { persistedSchema, STATE_VERSION, type PersistedState } from './schema';

export const STORAGE_KEY = 'snapcook_state_v2';
const LEGACY_KEY = 'snapcook_state_v1';
const BACKUP_KEY = 'snapcook_state_backup';

export const storage = {
  get(key: string): string | null {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  set(key: string, value: string): void {
    try { window.localStorage.setItem(key, value); } catch { /* private mode / quota, app keeps working in memory */ }
  },
  remove(key: string): void {
    try { window.localStorage.removeItem(key); } catch { /* ignore */ }
  },
};

export function initialState(): PersistedState {
  return persistedSchema.parse({
    version: STATE_VERSION,
    onboarded: false,
    onboardedAt: null,
    consent: { analytics: false, decidedAt: null },
    // Cupboard basics are asked for, never assumed.
    profile: { staples: [], maxMinutes: 120 },
    kitchen: { items: [], updatedAt: null },
    plan: null,
    planHistory: [],
    list: [],
    saved: [],
    cooked: [],
    feedback: [],
    inferenceOverrides: {},
    weights: [],
    checkIns: [],
    checkInSnoozedUntil: null,
    theme: 'system',
    crashReports: true,
    recipeUnlocks: { week: '', ids: [] },
    toggledAt: {},
  });
}

/** Validate unknown JSON into state. Unknown/invalid fields fall back to defaults individually. */
export function validate(raw: unknown): PersistedState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = persistedSchema.safeParse({ ...(raw as object), version: STATE_VERSION });
  return r.success ? r.data : null;
}

const V1_RULES: Record<string, DietRuleId[]> = {
  no_pork: ['no_pork'], no_beef: ['no_beef'], vegetarian: ['vegetarian'], vegan: ['vegan'],
  gluten_free: ['gluten_free'], halal: ['halal'], no_shellfish: ['allergy_shellfish'],
  nut_allergy: ['allergy_peanut', 'allergy_tree_nut'],
};

const V1_RECIPES: Record<string, string> = {
  'tomato-egg-skillet': 'tomato-egg-stir-fry', 'veggie-stir-fry-rice': 'veggie-fried-rice',
  'greek-yogurt-parfait': 'blueberry-yogurt-parfait', 'spinach-garlic-saute': 'garlic-spinach',
  'banana-oat-pancakes': 'banana-pancakes', 'banana-pancakes': 'two-ingredient-banana-pancakes',
  'zucchini-noodles': 'zucchini-garlic-noodles', 'mushroom-toast': 'garlic-mushroom-toast',
  'apple-peanut-butter-snack': 'apple-peanut-butter', 'rice-pudding': 'stovetop-rice-pudding',
  'berry-smoothie': 'berry-yogurt-smoothie',
};

/** Migrate the v1 MVP blob. Freshness-derived data is intentionally discarded. */
export function migrateV1(raw: unknown): PersistedState | null {
  if (!raw || typeof raw !== 'object') return null;
  const v1 = raw as Record<string, unknown>;
  const base = initialState();
  const profile = (v1.profile ?? {}) as Record<string, unknown>;
  const asArr = (x: unknown): unknown[] => (Array.isArray(x) ? x : []);
  const strs = (x: unknown) => asArr(x).filter((s): s is string => typeof s === 'string');

  const rules = [...new Set(strs(profile.dietRules).flatMap((r) => V1_RULES[r] ?? []))];
  const dislikes = strs(profile.dislikes).map(resolveIngredient).filter((x): x is IngredientId => !!x);
  const now = new Date().toISOString();
  const items = asArr(v1.inventory)
    .map((i) => resolveIngredient(String((i as Record<string, unknown>)?.name ?? '')))
    .filter((x): x is IngredientId => !!x)
    .filter((id, idx, all) => all.indexOf(id) === idx)
    .map((ingredientId) => ({ key: crypto.randomUUID(), ingredientId, useSoon: false, addedAt: now, source: 'manual' as const }));
  const mapRecipe = (id: string) => V1_RECIPES[id] ?? id;

  return validate({
    ...base,
    onboarded: v1.hasOnboarded === true,
    profile: { ...base.profile, rules, dislikes, household: Number(profile.householdSize) || base.profile.household },
    kitchen: { items, updatedAt: items.length ? now : null },
    saved: strs(v1.savedMeals).map(mapRecipe),
    cooked: asArr(v1.cookingHistory).map((h) => {
      const e = h as Record<string, unknown>;
      return { recipeId: mapRecipe(String(e?.recipeId)), at: String(e?.cookedAt ?? now), rating: null };
    }),
  });
}

export function load(): { state: PersistedState; recovered: boolean } {
  const raw = storage.get(STORAGE_KEY);
  if (raw) {
    try {
      const parsed = validate(JSON.parse(raw));
      if (parsed) return { state: parsed, recovered: false };
    } catch { /* fall through */ }
    storage.set(BACKUP_KEY, raw); // keep the unreadable blob for support, then start clean
    return { state: initialState(), recovered: true };
  }
  const legacy = storage.get(LEGACY_KEY);
  if (legacy) {
    try {
      const migrated = migrateV1(JSON.parse(legacy));
      if (migrated) {
        storage.set(STORAGE_KEY, JSON.stringify(migrated));
        storage.remove(LEGACY_KEY);
        return { state: migrated, recovered: false };
      }
    } catch { /* fall through */ }
  }
  return { state: initialState(), recovered: false };
}

/** The durable slice of any object holding app state. */
export function durable(s: PersistedState): PersistedState {
  const out = {} as Record<string, unknown>;
  for (const k of Object.keys(persistedSchema.shape)) out[k] = (s as Record<string, unknown>)[k];
  return out as PersistedState;
}

export function save(state: PersistedState): void {
  storage.set(STORAGE_KEY, JSON.stringify(durable(state)));
}

export function wipe(): void {
  for (const k of [STORAGE_KEY, LEGACY_KEY, BACKUP_KEY, 'snapcook_device_id']) storage.remove(k);
}
